import { PROGRAM_TYPE_BEHAVIOR_TRACKING } from 'src/constants/programs';
import { isBehaviorTrackingProgram } from 'src/utils/ProgramUtils';

import CoreAnalytics from './coreAnalytics';

/**
 * @typedef {import('./analyticsTypes').BehaviorTrackingDataRecordedEventPayload}
 *   BehaviorTrackingDataRecordedEventPayload
 */
/**
 * @typedef {import('./analyticsTypes').BehaviorTrackingProgramAddedEventPayload}
 *   BehaviorTrackingProgramAddedEventPayload
 */

/**
 * Analytics for the Behavior Tracking.
 *
 * Payloads carry ids and counts only. Program and target names are typed by clinicians and can
 * hold client details, so they never leave the app.
 */
class BehaviorTrackingAnalytics extends CoreAnalytics {
  EVENT_NAMES = {
    /**
     * Behavior data was saved in a session. `context` says whether it was scored directly on the
     * Behavior Tracking program or recorded while scoring a Skill Acquisition target — the comparison
     * the pilot measures. Distinct `programId`s give the number of Behavior Tracking programs in use;
     * distinct `skillTargetId`s show how widely "+ Behavior" is used across skill targets.
     */
    DATA_RECORDED: 'behavior_tracking_data_recorded',
    /**
     * A client program became Behavior Tracking: created as one, changed to one, copied from the
     * Library or duplicated. `source` says which. Counts additions only, not the current total.
     */
    PROGRAM_ADDED: 'behavior_tracking_program_added',
  };

  /**
   * Track behavior data saved in a session. Call only once the save has succeeded.
   *
   * @param {BehaviorTrackingDataRecordedEventPayload} payload
   */
  trackDataRecorded({
    programId, sessionId = null, context, entryCount, skillProgramId = null, skillTargetId = null,
  }) {
    if (!programId || !(entryCount > 0)) {
      return;
    }

    this.track(this.EVENT_NAMES.DATA_RECORDED, {
      programId,
      sessionId,
      context,
      entryCount,
      skillProgramId,
      skillTargetId,
      eventVersion: 1,
    });
  }

  /**
   * Track a client program becoming Behavior Tracking. Call once it is saved, with the type it had
   * when last saved, so that saving a program that already was Behavior Tracking sends nothing.
   *
   * @param {Object} program The saved program; only its id leaves the app.
   * @param {{ source: BehaviorTrackingProgramAddedEventPayload['source'], previousType?: string|null }} options
   */
  trackProgramAdded(program, { source, previousType = null }) {
    // Library templates have no client: nobody uses them until they are copied to one.
    if (!program?.id || !program.client || !isBehaviorTrackingProgram(program)
      || previousType === PROGRAM_TYPE_BEHAVIOR_TRACKING) {
      return;
    }

    this.track(this.EVENT_NAMES.PROGRAM_ADDED, {
      programId: program.id,
      source,
      previousType,
      eventVersion: 1,
    });
  }
}

export default new BehaviorTrackingAnalytics();
